/**
 * A group's membership and the people it will actually text are not the same
 * thing. A dynamic rule can miss someone, catch someone it shouldn't, or
 * include a person with no mobile number on file — so the API resolves the
 * difference and these are the shapes it returns.
 */

/** Where one person on a roster came from. */
export type RosterSource = 'rule' | 'added' | 'excluded'

export type RosterMember = {
  id: string
  displayName: string
  mail?: string
  jobTitle?: string
  department?: string
  /**
   * Whether a text would actually reach them. The API deliberately never sends
   * the number itself — the only thing this app needs to show is reachability,
   * so the number stays server-side.
   */
  hasMobile: boolean
  source: RosterSource
  /**
   * Excluded people only: where they would have come from had the exclusion not
   * applied. Absent means they were in neither the group nor its ".Add" overlay,
   * so the exclusion did nothing — usually the wrong ".Exclude" group.
   */
  origin?: RosterSource
}

/** The arithmetic behind a roster: baseline + added − excluded = effective. */
export type RosterCounts = {
  baseline: number
  added: number
  excluded: number
  effective: number
  textable: number
  unreachable: number
}

/** An ".Add"/".Exclude" group — never a send target, only ever named as the place an override lives. */
export type OverlayRef = { id: string; displayName: string }

export type GroupRoster = {
  id: string
  displayName: string
  description?: string
  /** A rule-derived group, whose membership can't be hand-edited. */
  isDynamic: boolean
  addGroup?: OverlayRef
  excludeGroup?: OverlayRef
  /** Who receives the text. */
  members: RosterMember[]
  /** Who the ".Exclude" overlay took out, so a short roster explains itself. */
  excluded: RosterMember[]
  counts: RosterCounts
}

/** One row of the groups list: the counts, without the people. */
export type GroupRosterSummary = {
  id: string
  displayName: string
  description?: string
  isDynamic: boolean
  hasOverlays: boolean
  counts: RosterCounts
  /** Set when this one group couldn't be read; the rest of the list still renders. */
  error?: string
}

/** True when a group's membership is overridden by an .Add/.Exclude overlay. */
export function hasOverlays(roster: GroupRoster): boolean {
  return roster.addGroup != null || roster.excludeGroup != null
}
